import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useFetchUserSuggestions } from 'Shared/utils/hooks/use-fetch-user-suggestions';
import { useFeature } from 'Shared/utils/hooks';

// Utils
import { getCurrentMoment, formatDate } from 'Shared/utils/DateTime.utils';

// Selectors
import { getUserSuggestionsByDay } from 'Shared/selectors/UserSuggestions.selectors';

// Types
import { Feature } from 'Shared/types/Organization.type';
import { UserSuggestionType } from 'Shared/types/UserSuggestion.type';

// Components
import { WpAvatar } from 'Wpds/components/avatar';
import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import UserSuggestionsList from './UserSuggestionsList';
import LoadingShimmer from '../LoadingShimmer/LoadingShimmer';

// Styles
import './UserSuggestions.scss';

const ADVERB_OPTIONS = [
  'abundant',
  'amazing',
  'awesome',
  'balanced',
  'brilliant',
  'count',
  'dynamic',
  'effective',
  'empowering',
  'energized',
  'epic',
  'excellent',
  'exceptional',
  'extraordinary',
  'fantastic',
  'focused',
  'fulfilling',
  'great',
  'growth-focused',
  'impactful',
  'innovative',
  'inspiring',
  'legendary',
  'magical',
  'matter',
  'meaningful',
  'memorable',
  'outstanding',
  'phenomenal',
  'powerful',
  'proactive',
  'productive',
  'purposeful',
  'remarkable',
  'rewarding',
  'shine',
  'significant',
  'special',
  'stellar',
  'successful',
  'transformative',
  'triumphant',
  'unforgettable',
  'vibrant',
  'worthwhile',
];

const UserSuggestions = () => {
  const { user, getFlag, updateFlag } = useCurrentUser();
  const { isLoading } = useFetchUserSuggestions();

  const today = formatDate(getCurrentMoment(), 'YYYY-MM-DD');

  const homeSuggestionsOpenState: { isOpen: boolean; updatedAt: string } = getFlag({
    flag: 'home_suggestions_open_state',
  });

  const [isOpen, setIsOpen] = useState(false);

  const [adverb] = useState(ADVERB_OPTIONS[Math.floor(Math.random() * ADVERB_OPTIONS.length)]);

  const { isActive: isKudosActive } = useFeature(Feature.kudos);

  // Get all suggestions from Redux
  const allSuggestions = useSelector(state =>
    getUserSuggestionsByDay(state, {
      day: today,
      dismissed: false,
    })
  );

  // Filter suggestions based on feature flags using useMemo
  const userSuggestions = useMemo(() => {
    if (!isKudosActive) {
      return allSuggestions.filter(suggestion => suggestion.type !== UserSuggestionType.provideKudos);
    }
    return allSuggestions;
  }, [allSuggestions, isKudosActive]);

  const hasSuggestions = userSuggestions.length > 0;
  const showLoading = isLoading && !hasSuggestions;

  const handleUpdateFlag = useCallback(
    (isOpen: boolean) => {
      const data = {
        isOpen,
        updatedAt: today,
      };
      updateFlag({ flag: 'home_suggestions_open_state', value: data });
    },
    [isOpen]
  );

  const handleButtonClick = useCallback(() => {
    setIsOpen(!isOpen);
    handleUpdateFlag(!isOpen);
  }, [isOpen]);

  const handleContainerClick = useCallback(() => {
    if (!isOpen) {
      setIsOpen(!isOpen);
      handleUpdateFlag(!isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    if (homeSuggestionsOpenState && homeSuggestionsOpenState?.updatedAt === today)
      setIsOpen(homeSuggestionsOpenState.isOpen);
    else setIsOpen(true);
  }, []);

  const baseClassName = 'UserSuggestions';
  const classNames = classnames(baseClassName, !isOpen ? `${baseClassName}--closed` : '');

  const Title = () => {
    if (showLoading) return <LoadingShimmer shape="rectangle" width="400px" height="28px" />;
    if (!hasSuggestions) return <h3>Hey {user.first_name}, you&apos;re awesome</h3>;
    return (
      <h3>
        Hey {user.first_name}, make today {adverb}
      </h3>
    );
  };
  const Description = () => {
    if (showLoading)
      return (
        <div className={`${baseClassName}__header__details__headings__desc--loading`}>
          <LoadingShimmer shape="rectangle" width="300px" height="20px" />
        </div>
      );
    if (!hasSuggestions)
      return (
        <p className={`${baseClassName}__header__details__headings__desc`}>
          You&apos;ve been showing great leadership skills and we&apos;re proud of you.
        </p>
      );

    return (
      <p className={`${baseClassName}__header__details__headings__desc`}>Take these actions to help your team grow</p>
    );
  };

  return (
    <div className={classNames} onClick={handleContainerClick}>
      <div className={`${baseClassName}__header`}>
        <div className={`${baseClassName}__header__details`}>
          <WpAvatar avatar={user.avatar} size="xl" />
          <div className={`${baseClassName}__header__details__headings`}>
            <Title />
            <Description />
          </div>
        </div>
        <div className={`${baseClassName}__header__collapse-btn`} onClick={e => e.stopPropagation()}>
          <WpButton onClick={handleButtonClick} variant="tertiary">
            <WpIcon name={isOpen ? 'minus' : 'plus'} />
          </WpButton>
        </div>
      </div>
      <WpCollapsible open={isOpen}>
        <div className={`${baseClassName}__body`}>
          <UserSuggestionsList userSuggestions={userSuggestions} />
          {/* <CompletedUserSuggestions /> */}
        </div>
      </WpCollapsible>
    </div>
  );
};

export default UserSuggestions;
